import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

import { ChevronRightIcon } from "@heroicons/react/24/outline";
import { useMutation } from "@tanstack/react-query";

import Button from "../Components/buttons/Button";
import FormDropzone from "../Components/forms/Dropzone";
import FormElementLabel from "../Components/forms/FormElementLabel";
import FormInput from "../Components/forms/FormInput";
import H3 from "../Components/forms/H3";
import { postCandidateApplication } from "../lib/api";
import PrivacyPolicyView from "../Views/PrivacyPolicyView";
import Dialog from "./Dialog";
import FormText from "./forms/FormText";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { FormProvider, useForm } from "react-hook-form";
import { schema } from "./forms/Schema";

const ApplicationForm: React.FC<{ jobAdId: string }> = ({ jobAdId }) => {
  const { t } = useTranslation(["ApplicationView", "form", "common"]);
  const [applicationSuccess, setApplicationSuccess] = useState<boolean>(false);
  const [showDialog, setShowDialog] = useState<boolean>(false);
  const [applicationError, setApplicationError] = useState<boolean>(false);

  const submitApplication = useMutation({
    mutationFn: (body: FormData) => postCandidateApplication(body, jobAdId),
    onSuccess: (response) => {
      if (response.ok) {
        setApplicationSuccess(true);
        setApplicationError(false);
        reset();
      } else {
        setApplicationError(true);
      }
    },
  });

  const onSubmit = (data: SchemaFormData) => {
    const formData = new FormData();
    Object.entries(data).forEach(([key, value]: [string, any]) => {
      if (
        key === "cv_file" ||
        key === "video_cv_file" ||
        (key === "other_document_file" && value)
      ) {
        const file = value[0] as File;
        if (file) formData.append(key, file, file.name);
      }
      formData.append(
        key,
        typeof value === "object" && value instanceof File
          ? value
          : String(value)
      );
    });

    submitApplication.mutate(formData);
  };

  type SchemaFormData = z.infer<typeof schema>;

  const methods = useForm<SchemaFormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: "",
      email: "",
      phone_number: "",
      terms: false,
    },
  });
  const reset = methods.reset;
  const checked = methods.watch("terms");

  useEffect(() => {
    methods.setFocus("name");
  }, [methods]);  

  return (
    <div className="isolate bg-white px-4 sm:py-10 lg:px-8">
      {applicationSuccess && (
        <div className="mx-auto max-w-xl text-center">
          {" "}
          <H3 text={t("ApplicationView:success")} />{" "}
        </div>
      )}
      {!applicationSuccess && (
        <>
          {/* <div className="text-center">
            <H3 text={t("ApplicationView:applyForm")} />
          </div> */}
          <FormProvider {...methods}>
            <form
              onSubmit={methods.handleSubmit(onSubmit)}
              className="mx-auto max-w-xl mt-2"
            >
              <div className="grid grid-cols-1 gap-x-8 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <FormInput
                    name={"name"}
                    placeholder={t("form:name")}
                    type="text"
                    errorMessage={t("form:nameError")}
                    label={t("form:name")}
                    required
                  />
                </div>
                <div>
                  <FormInput
                    name={"email"}
                    type={"email"}
                    placeholder={t("form:email")}
                    errorMessage={t("form:emailError")}
                    label={t("form:email")}
                    required
                  />
                </div>
                <div>
                  <FormInput
                    name={"phone_number"}
                    type="tel"
                    placeholder={t("form:phoneNumber")}
                    errorMessage={t("form:phoneNumberError")}
                    label={t("form:phoneNumber")}
                    required
                  />
                </div>
                <div className="sm:col-span-2 mb-3">
                  <FormElementLabel label={`${t("form:cv")} ${"*"}`} />
                  <FormDropzone
                    type="CV"
                    name={"cv_file"}
                    errorMessage={t("form:cvError")}
                  />
                </div>
                <div className="sm:col-span-2 mb-3">
                  <FormElementLabel label={t("form:video")} />
                  <FormDropzone type="Video" name={"video_cv_file"} />
                </div>
                <div className="sm:col-span-2 mb-3">
                  <FormElementLabel label={t("form:other")} />
                  <FormDropzone type="Other" name={"other_document_file"} />
                </div>
                <div className="sm:col-span-2 mb-3">
                  <FormInput
                    name={"message"}
                    placeholder={t("form:messagePlaceholder")}
                    type="text"
                    errorMessage={t("form:messageError")}
                    label={t("form:message")}
                  />                  
                </div>
              </div>
              <div className="flex my-6 flex-row items-baseline">
                <input
                  type="checkbox"
                  className="h-4 w-4 mr-4 relative top-[3px] rounded border-gray-300 text-primary focus:ring-primary"
                  {...methods.register("terms")}
                />
                <div className="flex flex-col justify-start">
                  <p className="leading-5">
                    {t("ApplicationView:consent")}{" "}
                    <button
                      className="cursor-pointer inline-flex flex-row justify-start space-x-1 font-medium underline underline-offset-2"
                      onClick={() => {
                        setShowDialog(true);
                      }}
                    >
                      {t("ApplicationView:readHere")}
                      <ChevronRightIcon width={16} />
                    </button>
                  </p>
                </div>
              </div>
              <div className="mt-10">
                <Button
                  type="submit"
                  text={t("ApplicationView:apply")}
                  disabled={!checked}
                  isLoading={submitApplication.isPending}
                />
                <div className="mt-5 text-center">
                  {(submitApplication.error || applicationError) && (
                    <H3
                      color="text-warning"
                      text={t("common:somethingWentWrong")}
                    />
                  )}
                </div>
              </div>
            </form>
          </FormProvider>
        </>
      )}
      <Dialog
        show={showDialog}
        onClose={() => setShowDialog(false)}
        content={<PrivacyPolicyView openInDialog={true} />}
      />
    </div>
  );
};

export default ApplicationForm;
